import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBaseUrl } from "@/helpers/fetch.helpers";
import { getJwtToken } from "@/helpers/storage.helpers";

export type ScheduleType = {
  name: string;
  displayName: string;
};

export type ResponseProfile = {
  name: string;
  id: string;
};

export type AssetMap = Record<string, string>;

export type BlockScheduleDetails = {
  ID: string;
  ScheduleID: string;
  DeviceID: string;
  ScheduleType: string;
  Magnitude: number;
  FirstEfaDay: string;
  LastEfaDay: string;
  ResponseProfileID: string;
  ResponseProfileName: string;
  ActiveEfaBlocks: number[];
  ActiveSetPeriods: unknown;
};

export type CreateBlockScheduleRequest = {
  assetId: string;
  start: string;
  end: string;
  magnitude: number;
  blocks: number[];
  scheduleType: string;
  responseProfileName: string;
  responseProfileID: string;
};

export const createBlockScheduleApi = createApi({
  reducerPath: "createBlockScheduleApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl("/block-input/api/"),
    prepareHeaders: (headers) => {
      headers.set("authorization", `Bearer ${getJwtToken()}`);
      return headers;
    },
  }),
  tagTypes: ["BlockSchedule"],
  endpoints: (build) => ({
    getScheduleType: build.query<ScheduleType[], void>({
      query: () => "schedule-type",
    }),
    getResponseProfile: build.query<ResponseProfile[], void>({
      query: () => "response-profile",
    }),
    getAssetMap: build.query<AssetMap, void>({
      query: () => "asset-map",
      transformResponse: (response: Record<string, string>) => {
        const inverted: Record<string, string> = {};
        Object.entries(response).forEach(([id, name]) => {
          inverted[name] = id;
        });
        return inverted;
      },
    }),
    createBlockSchedule: build.mutation<void, CreateBlockScheduleRequest>({
      query: (body) => ({
        url: `schedule/${body.assetId}`,
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
    deleteBlockSchedule: build.mutation<void, string>({
      query: (id) => ({
        url: `schedule/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetScheduleTypeQuery,
  useGetResponseProfileQuery,
  useGetAssetMapQuery,
  useCreateBlockScheduleMutation,
  useDeleteBlockScheduleMutation,
} = createBlockScheduleApi;
